import { useState, useMemo } from 'react';
import {
  getActiveCups,
  getUpcomingCups,
  getCompletedCups,
  getHallOfFame,
  getCupRecords,
  cupPeriodLabels,
  cupPeriodIcons,
  cupPeriodColors,
  cupPeriodBgColors,
  cupCategoryLabels,
  cupCategoryIcons,
  cupCategoryBadgeColors,
  cupGlobalStats,
  getTimeRemainingText,
  formatCupDateRange,
  classificationDimensions,
  type RfwCup,
  type CupStanding,
  type HallOfFameEntry,
  type EliminationMatch,
} from '@/mocks/rfwCups';

/* ─── Helpers ─── */
const positionEmojis: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };
const positionTrophyColors: Record<number, string> = { 1: 'bg-yellow-50 border-yellow-200', 2: 'bg-stone-50 border-stone-200', 3: 'bg-orange-50 border-orange-200' };

function getTrendIcon(trend: 'up' | 'down' | 'stable'): string {
  if (trend === 'up') return '▲';
  if (trend === 'down') return '▼';
  return '▬';
}

function getTrendColor(trend: 'up' | 'down' | 'stable'): string {
  if (trend === 'up') return 'text-emerald-600';
  if (trend === 'down') return 'text-red-500';
  return 'text-foreground-300';
}

function getChangeText(change: number): string {
  if (change > 0) return `+${change}`;
  return `${change}`;
}

function getChangeColor(change: number): string {
  if (change > 0) return 'text-emerald-600';
  if (change < 0) return 'text-red-500';
  return 'text-foreground-400';
}

function formatNum(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

/* ─── Rating Bar ─── */
function RatingBar({ value, label }: { value: number; label: string }) {
  const color = value >= 85 ? 'bg-emerald-500' : value >= 70 ? 'bg-amber-500' : 'bg-red-400';
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-foreground-500 w-16 shrink-0 text-right">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-background-200 overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${value}%` }}></div>
      </div>
      <span className="text-[10px] font-bold text-foreground-800 w-7 text-right">{value}</span>
    </div>
  );
}

/* ─── Standing Row ─── */
function StandingRow({ standing, isPodium }: { standing: CupStanding; isPodium?: boolean }) {
  return (
    <tr className={`border-b border-background-200/70 ${isPodium ? positionTrophyColors[standing.position] || '' : ''} hover:bg-background-100/70 transition-colors`}>
      <td className="py-2.5 px-3">
        <span className="text-sm font-bold">
          {standing.position <= 3 ? positionEmojis[standing.position] : `#${standing.position}`}
        </span>
      </td>
      <td className="py-2.5 px-3">
        <span className={`text-[10px] font-bold ${getChangeColor(standing.change)}`}>
          {getChangeText(standing.change)}
        </span>
      </td>
      <td className="py-2.5 px-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full overflow-hidden bg-background-100 shrink-0 border border-background-200/70">
            <img src={standing.participantLogo} alt={standing.participantName} className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-950 truncate">{standing.participantName}</p>
            <p className="text-[10px] text-foreground-500 truncate">{standing.participantSubtitle}</p>
          </div>
        </div>
      </td>
      <td className="py-2.5 px-3">
        <span className="text-sm font-bold text-foreground-950">{standing.points.toLocaleString()}</span>
      </td>
      <td className="py-2.5 px-3 hidden md:table-cell">
        <div className="flex items-center gap-1.5">
          <RatingBar value={standing.scoreRating} label="Sc" />
        </div>
      </td>
      <td className="py-2.5 px-3 hidden md:table-cell">
        <div className="flex items-center gap-1.5">
          <RatingBar value={standing.activityRating} label="Ac" />
        </div>
      </td>
      <td className="py-2.5 px-3 hidden lg:table-cell">
        <div className="flex items-center gap-1.5">
          <RatingBar value={standing.interactionRating} label="In" />
        </div>
      </td>
      <td className="py-2.5 px-3 hidden lg:table-cell">
        <div className="flex items-center gap-1.5">
          <RatingBar value={standing.participationRating} label="Pa" />
        </div>
      </td>
      <td className="py-2.5 px-3 hidden xl:table-cell">
        <div className="flex items-center gap-1.5">
          <RatingBar value={standing.growthRating} label="Cr" />
        </div>
      </td>
      <td className="py-2.5 px-3">
        <span className={`text-sm font-bold ${getTrendColor(standing.trend)}`}>{getTrendIcon(standing.trend)}</span>
      </td>
    </tr>
  );
}

/* ─── Cup Detail Section ─── */
function CupDetailCard({ cup }: { cup: RfwCup }) {
  const isActive = cup.status === 'active';
  const isCompleted = cup.status === 'completed';
  const top3 = cup.standings.slice(0, 3);
  const rest = cup.standings.slice(3);

  return (
    <div className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden">
      {/* Cup Header */}
      <div className={`px-5 py-4 bg-gradient-to-r ${cupPeriodBgColors[cup.period]} relative overflow-hidden`}>
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
        <div className="relative z-10">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-white/20 flex items-center gap-1`}>
              <i className={`${cupPeriodIcons[cup.period]} text-[9px]`}></i>
              {cupPeriodLabels[cup.period]}
            </span>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-white/20">
              {cupCategoryLabels[cup.category]}
            </span>
            {isActive && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/20 text-white text-[10px] font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                {getTimeRemainingText(cup.endDate)}
              </span>
            )}
            {isCompleted && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-white/20">
                Finalizada
              </span>
            )}
          </div>
          <h2 className="text-xl font-bold text-white mb-1">{cup.name}</h2>
          <p className="text-sm text-white/80 max-w-2xl">{cup.description}</p>
          <div className="flex items-center gap-4 mt-2">
            <span className="text-[11px] text-white/70">
              <i className="ri-calendar-line mr-1"></i>{formatCupDateRange(cup.startDate, cup.endDate)}
            </span>
            <span className="text-[11px] text-white/70">
              <i className="ri-group-line mr-1"></i>{cup.totalParticipants} participantes
            </span>
            {cup.elimination && (
              <span className="text-[11px] text-white/70">
                <i className="ri-brackets-line mr-1"></i>Eliminatoria ×{cup.elimination.totalParticipants}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Prizes */}
      {cup.prizes.length > 0 && (
        <div className="px-5 py-3 border-b border-background-200/70">
          <h4 className="text-xs font-extrabold text-foreground-950 uppercase tracking-wider mb-2">Premios</h4>
          <div className="flex flex-wrap gap-2">
            {cup.prizes.filter(p => p.positions.includes(1)).map((prize, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-background-100 text-[10px] font-medium text-foreground-700 border border-background-200/70">
                <span>{prize.emoji}</span>
                {prize.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Standings Table */}
      {cup.standings.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-background-200/70 bg-background-100/70">
                <th className="py-2.5 px-3 text-[10px] font-extrabold text-foreground-500 uppercase text-left">#</th>
                <th className="py-2.5 px-3 text-[10px] font-extrabold text-foreground-500 uppercase text-left">Δ</th>
                <th className="py-2.5 px-3 text-[10px] font-extrabold text-foreground-500 uppercase text-left">Participante</th>
                <th className="py-2.5 px-3 text-[10px] font-extrabold text-foreground-500 uppercase text-left">Puntos</th>
                <th className="py-2.5 px-3 text-[10px] font-extrabold text-foreground-500 uppercase text-left hidden md:table-cell">Score</th>
                <th className="py-2.5 px-3 text-[10px] font-extrabold text-foreground-500 uppercase text-left hidden md:table-cell">Activ.</th>
                <th className="py-2.5 px-3 text-[10px] font-extrabold text-foreground-500 uppercase text-left hidden lg:table-cell">Interac.</th>
                <th className="py-2.5 px-3 text-[10px] font-extrabold text-foreground-500 uppercase text-left hidden lg:table-cell">Partic.</th>
                <th className="py-2.5 px-3 text-[10px] font-extrabold text-foreground-500 uppercase text-left hidden xl:table-cell">Crec.</th>
                <th className="py-2.5 px-3 text-[10px] font-extrabold text-foreground-500 uppercase text-center">↕</th>
              </tr>
            </thead>
            <tbody>
              {cup.standings.map((s) => (
                <StandingRow key={s.participantId} standing={s} isPodium={s.position <= 3} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {cup.standings.length === 0 && (
        <div className="p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-trophy-line text-2xl text-foreground-300"></i>
          </div>
          <p className="text-sm font-semibold text-foreground-700 mb-1">Clasificación próximamente</p>
          <p className="text-xs text-foreground-500">{cup.totalParticipants} participantes confirmados</p>
        </div>
      )}
    </div>
  );
}

/* ─── Elimination Bracket ─── */
function BracketView({ cup }: { cup: RfwCup }) {
  const rounds = useMemo(() => {
    if (!cup.elimination) return [];
    const grouped: Record<number, EliminationMatch[]> = {};
    cup.elimination.matches.forEach(m => {
      if (!grouped[m.round]) grouped[m.round] = [];
      grouped[m.round].push(m);
    });
    return Object.entries(grouped).sort(([a], [b]) => Number(a) - Number(b));
  }, [cup.elimination]);

  if (!cup.elimination || rounds.length === 0) return null;

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
          <i className="ri-brackets-line text-sm text-violet-600"></i>
        </div>
        <h3 className="text-sm font-extrabold text-foreground-950 uppercase tracking-wider">Eliminatoria ×{cup.elimination.totalParticipants}</h3>
      </div>

      <div className="overflow-x-auto">
        <div className="flex gap-4 min-w-max pb-4">
          {rounds.map(([roundNum, matches]) => (
            <div key={roundNum} className="flex flex-col gap-3 min-w-[220px]">
              <div className="text-center mb-1">
                <span className="px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 text-[10px] font-extrabold uppercase">
                  {matches[0]?.roundName || `Ronda ${roundNum}`}
                </span>
              </div>
              {matches.map((match) => (
                <div key={match.matchNumber} className="rounded-lg bg-background-50 border border-background-200/70 p-3">
                  {/* Player 1 */}
                  <div className={`flex items-center gap-2 mb-2 pb-2 border-b border-background-200/70 ${match.winner === 1 ? 'opacity-100' : match.winner === 2 ? 'opacity-40' : 'opacity-100'}`}>
                    <div className="w-6 h-6 rounded-full overflow-hidden bg-background-100 shrink-0">
                      {match.participant1Logo ? (
                        <img src={match.participant1Logo} alt="" className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-background-200"><i className="ri-question-line text-[8px] text-foreground-300"></i></div>
                      )}
                    </div>
                    <span className="text-[10px] font-semibold text-foreground-800 truncate flex-1">{match.participant1Name}</span>
                    <span className="text-[10px] font-bold text-foreground-950">{match.participant1Score > 0 ? match.participant1Score.toLocaleString() : '-'}</span>
                  </div>
                  {/* Player 2 */}
                  <div className={`flex items-center gap-2 ${match.winner === 2 ? 'opacity-100' : match.winner === 1 ? 'opacity-40' : 'opacity-100'}`}>
                    <div className="w-6 h-6 rounded-full overflow-hidden bg-background-100 shrink-0">
                      {match.participant2Logo ? (
                        <img src={match.participant2Logo} alt="" className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-background-200"><i className="ri-question-line text-[8px] text-foreground-300"></i></div>
                      )}
                    </div>
                    <span className="text-[10px] font-semibold text-foreground-800 truncate flex-1">{match.participant2Name}</span>
                    <span className="text-[10px] font-bold text-foreground-950">{match.participant2Score > 0 ? match.participant2Score.toLocaleString() : '-'}</span>
                  </div>
                  {match.date && (
                    <p className="text-[9px] text-foreground-400 mt-1.5 text-center">{match.date}</p>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Hall of Fame Card ─── */
function HallOfFameCard({ entry }: { entry: HallOfFameEntry }) {
  return (
    <div className="rounded-xl bg-background-50 border border-background-200/70 p-4 hover:border-amber-300/50 transition-colors duration-200 group cursor-pointer">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-12 h-12 rounded-full overflow-hidden shrink-0 ring-2 ring-amber-200">
          <img src={entry.champion.participantLogo} alt={entry.champion.participantName} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <h4 className="font-semibold text-foreground-950 text-sm truncate">{entry.champion.participantName}</h4>
            <span className="text-base shrink-0">🥇</span>
          </div>
          <p className="text-[10px] text-foreground-500 mt-0.5">
            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium ${cupCategoryBadgeColors[entry.category]}`}>
              {cupCategoryLabels[entry.category]}
            </span>
            <span className="mx-1">·</span>
            {entry.cupName}
          </p>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${cupPeriodColors[entry.period]}`}>
          {cupPeriodLabels[entry.period]}
        </span>
      </div>

      <div className="flex items-center gap-2 mb-2">
        {classificationDimensions.map((dim) => {
          const rating = entry.champion[`${dim.key}Rating` as keyof CupStanding] as number;
          const color = rating >= 85 ? 'bg-emerald-500' : rating >= 70 ? 'bg-amber-500' : 'bg-red-400';
          return (
            <div key={dim.key} className="flex-1">
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-[8px] text-foreground-400">{dim.label}</span>
                <span className="text-[9px] font-bold text-foreground-700">{rating}</span>
              </div>
              <div className="h-1 rounded-full bg-background-200 overflow-hidden">
                <div className={`h-full rounded-full ${color}`} style={{ width: `${rating}%` }}></div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-background-200/70">
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-bold text-amber-600">{entry.champion.points.toLocaleString()} pts</span>
          {entry.record && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600 font-medium flex items-center gap-0.5">
              <i className="ri-vip-diamond-line text-[8px]"></i>{entry.recordValue}
            </span>
          )}
        </div>
        <span className="text-[10px] text-foreground-400">
          {new Date(entry.date).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
        </span>
      </div>
    </div>
  );
}

/* ─── Record Card ─── */
function RecordCard({ record }: { record: { category: string; label: string; value: string; holder: string; cupName: string; date: string; icon: string } }) {
  return (
    <div className="rounded-xl bg-background-50 border border-background-200/70 p-4 hover:border-accent-200/60 transition-colors duration-200">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
          <i className={`${record.icon} text-sm text-accent-600`}></i>
        </div>
        <div>
          <p className="text-xs font-extrabold text-foreground-950 uppercase">{record.label}</p>
          <p className="text-[10px] text-foreground-500">{record.category}</p>
        </div>
      </div>
      <p className="text-2xl font-black text-accent-600 mb-2">{record.value}</p>
      <p className="text-xs font-semibold text-foreground-800">{record.holder}</p>
      <p className="text-[10px] text-foreground-500 mt-1">{record.cupName}</p>
      <p className="text-[10px] text-foreground-400 mt-0.5">{record.date}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   PÁGINA PRINCIPAL
   ═══════════════════════════════════════════════════════════════════ */
export default function CopasPage() {
  const [activeTab, setActiveTab] = useState<'active' | 'upcoming' | 'halloffame' | 'records'>('active');

  const active = getActiveCups();
  const upcoming = getUpcomingCups();
  const hallOfFame = getHallOfFame();
  const records = getCupRecords();

  const tabs = [
    { id: 'active' as const, label: 'Campeonatos Activos', icon: 'ri-fire-line', count: active.length },
    { id: 'upcoming' as const, label: 'Próximas Copas', icon: 'ri-calendar-line', count: upcoming.length },
    { id: 'halloffame' as const, label: 'Hall of Fame', icon: 'ri-vip-diamond-line', count: hallOfFame.length },
    { id: 'records' as const, label: 'Récords', icon: 'ri-trophy-line', count: records.length },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Epic%20championship%20trophy%20arena%20with%20golden%20light%20rays%2C%20dramatic%20sports%20stadium%20atmosphere%2C%20warm%20amber%20and%20gold%20lighting%2C%20prestigious%20competition%20venue%2C%20cinematic%20wide%20shot%20with%20bokeh%20lights%20in%20background%2C%20winner%20podium%20silhouettes%2C%20majestic%20and%20inspiring%20atmosphere%2C%20no%20text&width=1600&height=500&seq=copas-hero-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-background-50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-semibold mb-5">
            <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse"></span>
            COPAS RFW Y CAMPEONATOS
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight">
            Copas <span className="text-accent-400">RFW</span> y Campeonatos
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            Competencia permanente que premia a los mejores en 5 categorías. Copas Semanales, Mensuales y Anuales con clasificación, eliminatorias y Hall of Fame. El motor de actividad de toda la plataforma RFW.
          </p>

          {/* Hero Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8 max-w-2xl mx-auto">
            {[
              { label: 'Copas Activas', value: cupGlobalStats.activeCups.toString(), icon: 'ri-fire-line' },
              { label: 'Participantes', value: cupGlobalStats.activeParticipantsNow.toString(), icon: 'ri-group-line' },
              { label: 'Campeones', value: cupGlobalStats.totalChampions.toString(), icon: 'ri-trophy-line' },
              { label: 'Récords', value: cupGlobalStats.recordsCount.toString(), icon: 'ri-vip-diamond-line' },
            ].map((stat, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                <i className={`${stat.icon} text-white/60 text-lg mb-1 block`}></i>
                <p className="text-xl font-black text-white">{stat.value}</p>
                <p className="text-[10px] text-white/60">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Classification Dimensions */}
      <section className="py-8 px-4 md:px-6 max-w-7xl mx-auto">
        <div className="rounded-2xl bg-background-100 border border-background-200/70 p-5">
          <h3 className="text-sm font-extrabold text-foreground-950 uppercase tracking-wider mb-4 text-center">
            ¿Cómo se Clasifica?
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {classificationDimensions.map((dim) => (
              <div key={dim.key} className="text-center p-3 rounded-lg bg-background-50 border border-background-200/50">
                <div className="w-9 h-9 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-2">
                  <i className={`${dim.icon} text-sm text-accent-600`}></i>
                </div>
                <p className="text-xs font-extrabold text-foreground-950 mb-1">{dim.label}</p>
                <p className="text-lg font-black text-accent-600">{dim.weight}%</p>
                <p className="text-[9px] text-foreground-500 mt-1 leading-relaxed">{dim.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="px-4 md:px-6 max-w-7xl mx-auto pb-4">
        <div className="flex items-center justify-center gap-1 mb-8">
          <div className="inline-flex bg-background-100 rounded-full p-1 flex-wrap justify-center">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-accent-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'
                }`}>{tab.count}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="px-4 md:px-6 max-w-7xl mx-auto pb-16">
        {activeTab === 'active' && (
          <div className="space-y-8">
            {active.map((cup) => (
              <div key={cup.id}>
                <CupDetailCard cup={cup} />
                {cup.elimination && <BracketView cup={cup} />}
              </div>
            ))}
            {active.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-trophy-line text-3xl text-foreground-300"></i>
                </div>
                <p className="text-sm font-semibold text-foreground-600">No hay campeonatos activos en este momento</p>
                <p className="text-xs text-foreground-500 mt-1">Revisa las próximas copas</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'upcoming' && (
          <div className="space-y-8">
            {upcoming.map((cup) => (
              <CupDetailCard key={cup.id} cup={cup} />
            ))}
          </div>
        )}

        {activeTab === 'halloffame' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {hallOfFame.map((entry, i) => (
              <HallOfFameCard key={`${entry.cupId}-${i}`} entry={entry} />
            ))}
          </div>
        )}

        {activeTab === 'records' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {records.map((record, i) => (
              <RecordCard key={i} record={record} />
            ))}
          </div>
        )}

        {/* Sistema de Premios */}
        <div className="mt-10 rounded-2xl bg-background-100 border border-background-200/70 p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
              <i className="ri-gift-line text-sm text-amber-600"></i>
            </div>
            <h3 className="text-sm font-extrabold text-foreground-950 uppercase tracking-wider">Sistema de Premios</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              { emoji: '🏆', label: 'Trofeos', desc: 'Trofeo digital + físico para el campeón de cada copa' },
              { emoji: '🥇', label: 'Insignias', desc: 'Insignias permanentes de Campeón, Subcampeón y Tercer Lugar' },
              { emoji: '👁️', label: 'Visibilidad', desc: 'Aparición destacada en la landing de RFW por 7 días' },
              { emoji: '💎', label: 'Landing', desc: 'Entrada al Muro de Campeones y Hall of Fame permanente' },
              { emoji: '📢', label: 'Reconocimiento', desc: 'Publicación en redes sociales de RFW + mención especial' },
            ].map((prize, i) => (
              <div key={i} className="text-center p-3 rounded-lg bg-background-50 border border-background-200/50">
                <span className="text-2xl mb-2 block">{prize.emoji}</span>
                <p className="text-xs font-extrabold text-foreground-950 mb-1">{prize.label}</p>
                <p className="text-[10px] text-foreground-500 leading-relaxed">{prize.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Cross-links */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { label: 'Rankings RFW', desc: 'Rankings por Score en 7 categorías', icon: 'ri-bar-chart-line', href: '/rankings', color: 'bg-rose-100 text-rose-600' },
            { label: 'Competencias', desc: 'Batallas 1v1 en vivo', icon: 'ri-sword-line', href: '/competencias', color: 'bg-amber-100 text-amber-600' },
            { label: 'Ganadores', desc: 'Historial completo de ganadores', icon: 'ri-trophy-line', href: '/ganadores', color: 'bg-emerald-100 text-emerald-600' },
          ].map((link, i) => (
            <a
              key={i}
              href={link.href}
              className="flex items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-accent-200/60 transition-all duration-200 group cursor-pointer"
            >
              <div className={`w-10 h-10 rounded-lg ${link.color} flex items-center justify-center`}>
                <i className={`${link.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-sm font-bold text-foreground-950">{link.label}</p>
                <p className="text-[10px] text-foreground-500">{link.desc}</p>
              </div>
              <i className="ri-arrow-right-line text-foreground-300 ml-auto group-hover:text-accent-600 transition-colors"></i>
            </a>
          ))}
        </div>

        {/* Regla Final */}
        <div className="mt-10 p-8 rounded-2xl bg-accent-100/70 border border-accent-200/50 text-center">
          <div className="w-14 h-14 rounded-full bg-accent-500 flex items-center justify-center mx-auto mb-4">
            <i className="ri-fire-line text-2xl text-white"></i>
          </div>
          <h3 className="text-lg font-black text-foreground-950 mb-2">Regla Fundamental de Copas RFW</h3>
          <p className="text-sm text-foreground-700 max-w-lg mx-auto leading-relaxed">
            Las Copas RFW deben convertirse en uno de los principales motores de actividad de toda la plataforma.
            Cada semana hay una nueva oportunidad de ganar. Cada mes, una nueva corona. Cada año, la gloria eterna.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-5">
            {['Comunidad', 'Participación', 'Constancia', 'Crecimiento', 'Entretenimiento', 'Interacción'].map((pillar) => (
              <span key={pillar} className="px-3 py-1.5 rounded-full bg-accent-200/50 text-accent-800 text-xs font-bold">
                {pillar}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Footer spacer */}
      <div className="h-16 md:h-20"></div>
    </div>
  );
}